// Bildirishnomalar — xsib-core `notify` moduli bilan haqiqiy kontrakt.
// Manba: `notify/internal/{Template,Subscription,Message,QuietHours,TelegramLink}Controller.java`.
//
// ⚠️ Bu yerda `OperationResult` YO'Q — CoA/tarif kabi "baza + faol sessiya"
// ikki qadamli modeli notify'ga tegishli emas (CLAUDE.md §3.2 shu loyihaning
// o'zi uchun, bu yerda boshqa modul). Shablon/obuna/sokin soat — oddiy CRUD,
// backend to'g'ridan-to'g'ri resursni yoki 204'ni qaytaradi.
//
// ⚠️ Bot TOKENI hech qanday DTO'da yo'q — `TelegramLinkController` javadoc'i:
// faqat `LinkCode.fullCode()` (foydalanuvchi kodi, 15 daqiqada eskiradi) va
// `linked: boolean` holati qaytadi, `chat_id` esa ATAYLAB berilmaydi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { syntheticTotal } from '@/shared/api/pageResult'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const CHANNELS = ['SMS', 'TELEGRAM', 'EMAIL'] as const
export type Channel = (typeof CHANNELS)[number]

/** WhatsApp sends only Meta-approved templates, so it appears in the send log and quiet hours, not in templates or subscriptions. */
export const DELIVERY_CHANNELS = [...CHANNELS, 'WHATSAPP'] as const
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number]

/** Panel qo'llab-quvvatlaydigan uch til — `app/i18n` bilan bir xil ro'yxat */
export const TEMPLATE_LOCALES = ['uz', 'ru', 'en'] as const

/**
 * Shablon kodining SHAKLI — server bilan AYNI qoida.
 *
 * ⚠️ Manba nusxasi: `TemplateResource.TemplateRequest#code` dagi
 * `@Pattern`. Ikki joyda turishining sababi `TemplateFormModal` dagi
 * izohda: server — shartnoma, panel — maydon yonidagi xato. Ular
 * AJRALIB KETMASLIGI uchun `tester-round2.mjs` ikkalasini o'qib
 * solishtiradi.
 *
 * ⚠️ Bazada CHECK YO'Q (`V202608042300__notify_init.sql` — `code TEXT
 * NOT NULL`, xolos), ya'ni bu yagona qorovul. Staging'da mavjud
 * yagona kod — `c` (o'lchandi 2026-08-15) va u naqshga MOS, ya'ni
 * qoida eski ma'lumotni buzmaydi.
 */
export const TEMPLATE_CODE = /^[a-z0-9]+([._-][a-z0-9]+)*$/
export const TEMPLATE_CODE_MAX = 64
export type TemplateLocale = (typeof TEMPLATE_LOCALES)[number]

// --- Shablonlar ---------------------------------------------------------------

const templateSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  code: z.string(),
  channel: z.enum(CHANNELS),
  locale: z.string(),
  subject: z.string().nullish(),
  body: z.string(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface TemplateView {
  id: string
  code: string
  channel: Channel
  /** Backend erkin satr qabul qiladi — bu yerda `uz`/`ru`/`en` dan tashqarisi ham bo'lishi mumkin (eski yozuv) */
  locale: string
  subject: string | null
  body: string
  updatedAt: string | null
}

function toTemplate(raw: z.infer<typeof templateSchema>): TemplateView {
  return {
    id: raw.id,
    code: raw.code,
    channel: raw.channel,
    locale: raw.locale,
    subject: raw.subject ?? null,
    body: raw.body,
    updatedAt: raw.updatedAt ?? null,
  }
}

/** Hammasi — panel ularni `code` + `channel` bo'yicha o'zi guruhlaydi (uch tilni yonma-yon ko'rsatish uchun) */
export async function fetchTemplates(): Promise<TemplateView[]> {
  const response = await api.get<unknown>('/api/notify/templates')
  return z.array(templateSchema).parse(response.data).map(toTemplate)
}

export interface TemplateDraft {
  code: string
  channel: Channel
  locale: string
  subject?: string | null
  body: string
}

/** Yaratadi yoki (code, channel, locale) bo'yicha yangilaydi — idempotent (backend javadoc'i) */
export async function upsertTemplate(draft: TemplateDraft): Promise<TemplateView> {
  const response = await api.post<unknown>('/api/notify/templates', {
    code: draft.code,
    channel: draft.channel,
    locale: draft.locale,
    subject: draft.subject?.trim() || null,
    body: draft.body,
  })
  return toTemplate(templateSchema.parse(response.data))
}

export async function deleteTemplate(id: string): Promise<void> {
  await api.delete(`/api/notify/templates/${id}`)
}

// --- Obunalar -------------------------------------------------------------------

const subscriptionSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  eventType: z.string(),
  channel: z.enum(CHANNELS),
  templateCode: z.string(),
  priority: z.number(),
  enabled: z.boolean(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface SubscriptionView {
  id: string
  eventType: string
  channel: Channel
  templateCode: string
  /** Kichikroq raqam — yuqori ustuvorlik (masalan Telegram=1, SMS=2 — zaxira) */
  priority: number
  enabled: boolean
}

function toSubscription(raw: z.infer<typeof subscriptionSchema>): SubscriptionView {
  return {
    id: raw.id,
    eventType: raw.eventType,
    channel: raw.channel,
    templateCode: raw.templateCode,
    priority: raw.priority,
    enabled: raw.enabled,
  }
}

/** Hammasi — panel `eventType` bo'yicha o'zi guruhlaydi (ustuvorlik zanjirini ko'rsatish uchun) */
export async function fetchSubscriptions(): Promise<SubscriptionView[]> {
  const response = await api.get<unknown>('/api/notify/subscriptions')
  return z.array(subscriptionSchema).parse(response.data).map(toSubscription)
}

export interface SubscriptionDraft {
  eventType: string
  channel: Channel
  templateCode: string
  priority: number
  enabled: boolean
}

export async function createSubscription(draft: SubscriptionDraft): Promise<SubscriptionView> {
  const response = await api.post<unknown>('/api/notify/subscriptions', draft)
  return toSubscription(subscriptionSchema.parse(response.data))
}

/** Faqat ustuvorlik va yoqilgan-holati tahrirlanadi — `eventType`/`channel`/`templateCode` YANGI obuna yaratishni talab qiladi */
export async function updateSubscription(
  id: string,
  values: { priority: number; enabled: boolean },
): Promise<SubscriptionView> {
  const response = await api.put<unknown>(`/api/notify/subscriptions/${id}`, values)
  return toSubscription(subscriptionSchema.parse(response.data))
}

export async function deleteSubscription(id: string): Promise<void> {
  await api.delete(`/api/notify/subscriptions/${id}`)
}

// --- Yuborish jurnali -------------------------------------------------------------

/*
  ⛔ SXEMA JONLI JAVOBGA MOS EMAS EDI — SAHIFA ABADIY SKELETONDA QOLARDI
  (Staging'da o'lchandi, 2026-08-24).

  `GET /api/notify/messages` 200 va 25 ta qator qaytarardi, sahifa esa
  hech qachon chizilmasdi: `messagePageSchema.parse` istisno tashlaydi,
  so'rov rad etiladi, `DataTable` yuklanish holatida qotib qoladi va
  XATO HECH QAYERDA KO'RINMAYDI.

  Ikkita nomuvofiqlik bor edi, har biri yolg'iz o'zi yetarli:

    1. `eventId: z.number()` — server UUID SATR yuboradi
       (`MessageView.eventId` — `java.util.UUID`).
    2. `locale: z.string()` MAJBURIY edi — qiymat `null` bo'lganda
       Jackson maydonni javobga UMUMAN yozmaydi
       (`xsib-notify/application.yml`: `default-property-inclusion:
       non_null`), ya'ni panelga `null` emas, MAYDON YO'Q holat keladi.

  ⚠️ Ikkinchisi `null-contract.mjs` da hujjatlashtirilgan SINF: 17 ta
  servisda `non_null` yoqilgan va har `null` bo'la oladigan maydon
  `.nullish()` bo'lishi SHART. `subject`/`sentAt`/`providerMessageId`
  bu yerda allaqachon to'g'ri yozilgan edi — `locale` esa unutilgan.
*/
const messageSchema = z.object({
  id: uuidSchema,
  eventId: uuidSchema,
  eventType: z.string(),
  channel: z.enum(DELIVERY_CHANNELS),
  recipient: z.string(),
  templateCode: z.string(),
  locale: z.string().nullish(),
  subject: z.string().nullish(),
  body: z.string(),
  state: z.string(),
  attempts: z.number(),
  nextAttemptAt: instantSchema.nullish(),
  sentAt: instantSchema.nullish(),
  providerMessageId: z.string().nullish(),
  lastError: z.string().nullish(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
})

export interface MessageView {
  id: string
  eventType: string
  channel: DeliveryChannel
  /** ⚠️ TO'LIQ qiymat (telefon/chat_id/email), MASKALANMAGAN — backend javadoc'i: operator yetkazish muammosini tekshirishi kerak */
  recipient: string
  templateCode: string
  /** `null` — server maydonni yubormadi (qiymati bo'sh, `non_null`) */
  locale: string | null
  subject: string | null
  state: string
  attempts: number
  sentAt: string | null
  providerMessageId: string | null
  lastError: string | null
  createdAt: string
}

function toMessage(raw: z.infer<typeof messageSchema>): MessageView {
  return {
    id: raw.id,
    eventType: raw.eventType,
    channel: raw.channel,
    recipient: raw.recipient,
    templateCode: raw.templateCode,
    locale: raw.locale ?? null,
    subject: raw.subject ?? null,
    state: raw.state,
    attempts: raw.attempts,
    sentAt: raw.sentAt ?? null,
    providerMessageId: raw.providerMessageId ?? null,
    lastError: raw.lastError ?? null,
    createdAt: raw.createdAt,
  }
}

const messagePageSchema = z.object({
  rows: z.array(messageSchema),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
})

/**
 * Javobni panel shakliga aylantirish — CHAQIRUV JOYIDAN AJRATILGAN.
 *
 * Sabab `null-contract.mjs` 4-bo'limidagi bilan bir xil: naqsh skaneri
 * faqat MATNNI ko'radi, ya'ni sxema "to'g'ri yozilgan" bo'lib turib,
 * jonli javobda baribir yiqilishi mumkin. Shu funksiya eksport
 * qilingani uchun qorovul unga jonli javobning AYNI qatorini bera
 * oladi (`tests/notify-message-shape.mjs`) — tarmoqsiz va bazasiz.
 */
export function parseMessagePage(raw: unknown): { rows: MessageView[]; hasNext: boolean } {
  const parsed = messagePageSchema.parse(raw)
  return { rows: parsed.rows.map(toMessage), hasNext: parsed.hasNext }
}

export interface MessageFilter {
  channel?: DeliveryChannel | null
  state?: string | null
  /** ISO-8601 UTC — MAJBURIY (backend `@RequestParam Instant from/to`) */
  from: string
  to: string
}

/**
 * Yuborish jurnali sahifasi. Filtr YOPILGAN qiymat sifatida beriladi —
 * `AuditPage`/`VenueJournalPage` bilan bir xil andoza (`useMemo` chaqiruv joyida).
 */
export function fetchMessagePage(
  filter: MessageFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<MessageView>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/notify/messages', {
      signal,
      params: {
        ...(filter.channel ? { channel: filter.channel } : {}),
        ...(filter.state ? { state: filter.state } : {}),
        from: filter.from,
        to: filter.to,
        // Backend sahifani 0 dan sanaydi — boshqa ro'yxat endpointlari bilan bir xil andoza
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    const parsed = parseMessagePage(response.data)
    return {
      items: parsed.rows,
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

/** O'lik xabarlar — "hozir nima buzilgan" ro'yxati, SANA FILTRISIZ (backend javadoc'i) */
export function fetchDeadLetters(): (
  state: TableState,
  signal: AbortSignal,
) => Promise<PageResult<MessageView>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/notify/messages/dead', {
      signal,
      params: { page: state.page - 1, size: state.pageSize },
    })
    const parsed = parseMessagePage(response.data)
    return {
      items: parsed.rows,
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

// --- Sokin soatlar ----------------------------------------------------------------

const quietHoursSchema = z.object({
  channel: z.enum(DELIVERY_CHANNELS),
  start: z.string(),
  end: z.string(),
  enabled: z.boolean(),
})

export interface QuietHoursView {
  channel: DeliveryChannel
  /** `HH:mm:ss` — antd `TimePicker` shu shaklni tushunadi */
  start: string
  end: string
  enabled: boolean
}

/** Qator yo'q kanal = cheklovsiz (`QuietHoursRepository` javadoc'i) */
export async function fetchQuietHours(): Promise<QuietHoursView[]> {
  const response = await api.get<unknown>('/api/notify/quiet-hours')
  return z.array(quietHoursSchema).parse(response.data)
}

export async function upsertQuietHours(draft: QuietHoursView): Promise<QuietHoursView> {
  const response = await api.post<unknown>('/api/notify/quiet-hours', draft)
  return quietHoursSchema.parse(response.data)
}

export async function deleteQuietHours(channel: DeliveryChannel): Promise<void> {
  await api.delete(`/api/notify/quiet-hours/${channel}`)
}

// --- Telegram bog'lash (abonent bo'yicha) ------------------------------------------

const linkCodeSchema = z.object({
  fullCode: z.string(),
  subscriberId: uuidSchema,
  expiresAt: instantSchema,
})

export interface LinkCode {
  /** Abonent botga `/start <fullCode>` shaklida yuboradi */
  fullCode: string
  expiresAt: string
}

/** Bir martalik bog'lash kodi chiqaradi — 15 daqiqadan keyin eskiradi (backend default) */
export async function issueTelegramLinkCode(subscriberId: string): Promise<LinkCode> {
  const response = await api.post<unknown>(`/api/notify/telegram/subscribers/${subscriberId}/link-codes`)
  const parsed = linkCodeSchema.parse(response.data)
  return { fullCode: parsed.fullCode, expiresAt: parsed.expiresAt }
}

/** ⚠️ `chat_id` ATAYLAB qaytarilmaydi — faqat "bog'langanmi" (backend javadoc'i) */
export async function fetchTelegramLinkStatus(subscriberId: string): Promise<boolean> {
  const response = await api.get<unknown>(`/api/notify/telegram/subscribers/${subscriberId}`)
  return z.object({ linked: z.boolean() }).parse(response.data).linked
}

export async function unlinkTelegram(subscriberId: string): Promise<void> {
  await api.delete(`/api/notify/telegram/subscribers/${subscriberId}`)
}
